/**
 * Sector operations — Member Construction Reports Group (R39 B9.b2).
 * Read-only preview of 5 CON_REPORT_KINDS for a project and range <= 92 days.
 * Displays first 50 rows + total count, and link to full report on desk (?tab=con-reports).
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import type { NlsKey } from '../../../nls/strings_nls'
import { Select } from '../../Common'
import type { SelectOption } from '../../Common'
import { useWorkspace } from '../../../context/useWorkspace'
import { workspaceTodayKey } from '../../../services/repeat/keys'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import { listConProjects } from '../../../services/conProjectsService'
import { getConReport } from '../../../services/conReportsService'
import {
  CON_REPORT_KINDS,
  MAX_CON_REPORT_RANGE_DAYS,
} from '../../../types/ops'
import type { ConProject, ConReportKind, ConReportResult } from '../../../types/ops'
import styles from './MemberConReportsGroup.module.css'

export interface MemberConReportsGroupProps {
  workspaceId: string
  scope: {
    ready: boolean
    restricted: boolean
    allowedPlaceIds?: ReadonlySet<string>
  }
}

const unwrapVal = (v: unknown): string =>
  v != null && typeof v === 'object' && 'target' in v
    ? String((v as { target: { value?: unknown } }).target.value ?? '')
    : String(v ?? '')

function getDefaultLast7Days(toDay: string): { fromDay: string; toDay: string } {
  const [y, m, d] = toDay.split('-').map(Number)
  const toUtc = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  const fromUtc = new Date(toUtc)
  fromUtc.setUTCDate(fromUtc.getUTCDate() - 6)

  const format = (date: Date) => {
    const yStr = date.getUTCFullYear()
    const mStr = String(date.getUTCMonth() + 1).padStart(2, '0')
    const dtStr = String(date.getUTCDate()).padStart(2, '0')
    return `${yStr}-${mStr}-${dtStr}`
  }

  return { fromDay: format(fromUtc), toDay }
}

const REPORT_KIND_NLS_KEY: Record<ConReportKind, NlsKey> = {
  APPROVED_DPR: 'NLSMemberOpsConReportKindApprovedDpr',
  HINDRANCE_AGEING: 'NLSMemberOpsConReportKindHindranceAgeing',
  OVERDUE_QUALITY: 'NLSMemberOpsConReportKindOverdueQuality',
  ATTENDANCE_PROVISIONAL: 'NLSMemberOpsConReportKindAttendanceProvisional',
  HANDOVER_INDEX: 'NLSMemberOpsConReportKindHandoverIndex',
}

function capDateRange(
  fromDay: string,
  toDay: string,
): { fromDay: string; toDay: string; wasCapped: boolean } {
  const fromDate = new Date(`${fromDay}T00:00:00Z`)
  const toDate = new Date(`${toDay}T00:00:00Z`)
  const diffDays = Math.round(
    (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24),
  )
  if (diffDays > MAX_CON_REPORT_RANGE_DAYS) {
    const capped = new Date(fromDate)
    capped.setDate(capped.getDate() + MAX_CON_REPORT_RANGE_DAYS)
    const y = capped.getFullYear()
    const m = String(capped.getMonth() + 1).padStart(2, '0')
    const dt = String(capped.getDate()).padStart(2, '0')
    return { fromDay, toDay: `${y}-${m}-${dt}`, wasCapped: true }
  }
  return { fromDay, toDay, wasCapped: false }
}

export function MemberConReportsGroup({
  workspaceId,
  scope,
}: MemberConReportsGroupProps): React.ReactElement {
  const { t } = useNls()
  const { workspaces } = useWorkspace()
  const workspace = workspaces.find((w) => w.id === workspaceId)
  const defaultToDay = useMemo(
    () => workspaceTodayKey('DAILY', workspace?.timezone),
    [workspace?.timezone],
  )

  const [projects, setProjects] = useState<ConProject[]>([])
  const [userSelectedProjectId, setUserSelectedProjectId] = useState<string>('')
  const [selectedKind, setSelectedKind] = useState<ConReportKind>('APPROVED_DPR')
  const [dates] = useState<{ fromDay: string; toDay: string }>(() =>
    getDefaultLast7Days(defaultToDay),
  )
  const [reportResult, setReportResult] = useState<ConReportResult | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Load readable projects
  useEffect(() => {
    if (!workspaceId || !scope.ready) return
    let active = true

    async function loadProjects() {
      try {
        const res = await listConProjects({ workspaceId })
        if (active) {
          setProjects(res.projects || [])
        }
      } catch (err) {
        if (active) setErrorMsg(getApiErrorMessage(err))
      }
    }

    void loadProjects()
    return () => {
      active = false
    }
  }, [workspaceId, scope.ready])

  const readableProjects = useMemo(() => {
    if (!scope.restricted || !scope.allowedPlaceIds) return projects
    return projects.filter((p) => scope.allowedPlaceIds?.has(p.placeId))
  }, [projects, scope.restricted, scope.allowedPlaceIds])

  const effectiveProjectId = useMemo(() => {
    if (userSelectedProjectId && readableProjects.some((p) => p.id === userSelectedProjectId)) {
      return userSelectedProjectId
    }
    return readableProjects[0]?.id || ''
  }, [readableProjects, userSelectedProjectId])

  const { fromDay, toDay, wasCapped } = useMemo(() => {
    return capDateRange(dates.fromDay, dates.toDay)
  }, [dates.fromDay, dates.toDay])

  // Fetch report
  useEffect(() => {
    if (!workspaceId || !effectiveProjectId) {
      return
    }
    let active = true

    void Promise.resolve().then(async () => {
      if (!active) return
      setLoading(true)
      setErrorMsg(null)
      try {
        const res = await getConReport({
          workspaceId,
          projectId: effectiveProjectId,
          kind: selectedKind,
          fromDay,
          toDay,
        })
        if (active) {
          setReportResult(res)
        }
      } catch (err) {
        if (active) setErrorMsg(getApiErrorMessage(err))
      } finally {
        if (active) setLoading(false)
      }
    })

    return () => {
      active = false
    }
  }, [workspaceId, effectiveProjectId, selectedKind, fromDay, toDay])

  const projectOptions: SelectOption<string>[] = useMemo(() => {
    return readableProjects.map((p) => ({
      value: p.id,
      label: `${p.code} — ${p.name}`,
    }))
  }, [readableProjects])

  const kindOptions: SelectOption<string>[] = useMemo(() => {
    return CON_REPORT_KINDS.map((k) => ({
      value: k,
      label: t(REPORT_KIND_NLS_KEY[k]),
    }))
  }, [t])

  const effectiveResult = effectiveProjectId ? reportResult : null

  const displayedRows = useMemo(() => {
    if (!effectiveResult || !Array.isArray(effectiveResult.rows)) return []
    return effectiveResult.rows.slice(0, 50)
  }, [effectiveResult])

  const totalRowCount = useMemo(() => {
    if (!effectiveResult || !Array.isArray(effectiveResult.rows)) return 0
    return effectiveResult.rows.length
  }, [effectiveResult])

  return (
    <div className={styles.group} data-testid="member-con-reports-group">
      <div className={styles.groupHeader}>
        <h3 className={styles.groupTitle}>{t('NLSMemberOpsConReportsTitle')}</h3>
        <a
          href="?tab=con-reports"
          className={styles.deskLink}
          data-testid="con-reports-desk-link"
        >
          {t('NLSMemberOpsConReportsDeskLink')}
        </a>
      </div>

      {wasCapped && (
        <div className={styles.cappedBanner} role="status">
          {t('NLSMemberOpsConReportsDaysCappedNotice', {
            maxDays: String(MAX_CON_REPORT_RANGE_DAYS),
          })}
        </div>
      )}

      {errorMsg && (
        <div role="alert" className={styles.emptyState}>
          {errorMsg}
        </div>
      )}

      {/* Selectors */}
      <div className={styles.filterRow}>
        <div data-testid="con-reports-project-select">
          <Select
            ariaLabel={t('NLSMemberOpsDprProjectPickerLabel')}
            value={effectiveProjectId}
            onChange={(val) => setUserSelectedProjectId(unwrapVal(val))}
            options={projectOptions}
            disabled={readableProjects.length === 0}
          />
        </div>
        <div data-testid="con-reports-kind-select">
          <Select
            ariaLabel={t('NLSMemberOpsConReportsKindLabel')}
            value={selectedKind}
            onChange={(val) =>
              setSelectedKind(unwrapVal(val) as ConReportKind)
            }
            options={kindOptions}
          />
        </div>
      </div>

      {/* Results */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.emptyState}>{t('NLSCommonStatusLoading')}</div>
        ) : !effectiveResult || totalRowCount === 0 ? (
          <div className={styles.emptyState}>
            {t('NLSMemberOpsConReportsEmpty')}
          </div>
        ) : (
          <>
            <span className={styles.rowCountInfo}>
              {t('NLSMemberOpsConReportsRowsShown', {
                shown: String(displayedRows.length),
                total: String(totalRowCount),
              })}
            </span>
            <ul className={styles.rowsList}>
              {displayedRows.map((row, idx) => {
                const rec =
                  row && typeof row === 'object'
                    ? (row as Record<string, unknown>)
                    : {}
                const title = String(
                  rec.element || rec.title || rec.date || rec.name || rec.id || `Row ${idx + 1}`,
                )
                const secondary = String(
                  rec.state || rec.status || rec.shiftId || rec.kind || '',
                )

                return (
                  <li
                    key={String(rec.id ?? idx)}
                    className={styles.reportRowCard}
                    data-testid={`con-report-row-${idx}`}
                  >
                    <span>{title}</span>
                    {secondary && <span>{secondary}</span>}
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
